import * as pmtiles from 'pmtiles';
import { Protocol } from 'pmtiles';

import type { Coordinate, MapInstance, MapRendererApp, StyleLayer, StyleDefinition, SurroundConfig } from '../types.ts';
import { calculatePanClampBounds, DEFAULT_MAX_PAN_MILES } from './calculatePanClampBounds.ts';
import { extractCategories } from './conflictingLayers.ts';
import { findSurroundInsertionPoint, findSurroundLabelInsertionPoint } from './findSurroundInsertionPoint.ts';
import highContrastStyle from './surroundStyles/highContrastStyle.ts';
import positronStyle, { localizedTextField } from './surroundStyles/positronStyle.ts';
import type { SurroundLayer, SurroundStyle } from './surroundStyles/surroundStyle.ts';

const ABOVE_BUILDING_LAYER_IDS = new Set(['positron-railway-name']);

// Surround style layer IDs all share this prefix, which lets us find and
// remove them as a group when re-rendering for a style change.
const SURROUND_LAYER_PREFIX = 'positron-';

const addLayer = (map: MapInstance, layer: StyleLayer, beforeLayerId?: string): void => {
  if (beforeLayerId === undefined) {
    map.addLayer(layer as never);
    return;
  }
  map.addLayer(layer as never, beforeLayerId);
};

type TileData = {
  accessibilityDescriptions: Record<string, string>;
};

// addSurroundTiles reruns on every venue load, but its bus listeners must only
// ever be subscribed once (see mapRenderer.tsx). Rather than calling app.bus.on
// itself, each run hands its handlers (bound to its own map/state) back to the
// caller via setHandlers, so venue switches replace them instead of stacking up
// duplicate listeners.
export type SurroundToggleHandler = (payload?: { highContrast?: boolean }) => void;
export type LangChangedHandler = () => void | Promise<void>;
export type SetSurroundHandlers = (handlers: {
  onToggle: SurroundToggleHandler;
  onLangChanged: LangChangedHandler;
}) => void;

type VenueDataWithBaseVenueId = {
  baseVenueId: string;
};

type AppWithRuntimeBits = MapRendererApp & {
  log: {
    sublog(name: string): {
      warn(message: string): void;
      error(message: string, error?: unknown): void;
    };
  };
  i18n: () => {
    language?: string;
  };
};

// Adds the surround description within pmtiles metadata to a child of the map for use as
// an invisible aria-describedby div
const setMapDescription = async (
  tileProtocol: Protocol,
  tileUrl: string,
  langCode = 'en',
  app: MapRendererApp,
): Promise<void> => {
  try {
    const surroundTilesArchive = new pmtiles.PMTiles(tileUrl);
    tileProtocol.add(surroundTilesArchive);

    const metaData = (await surroundTilesArchive.getMetadata()) as {
      description: string;
    };

    const descriptionOptions = JSON.parse(metaData.description) as TileData;

    const accessibleDescription = descriptionOptions.accessibilityDescriptions[langCode]
      ? descriptionOptions.accessibilityDescriptions[langCode]
      : descriptionOptions.accessibilityDescriptions.en;

    const mapCanvasDescription = document.getElementById('MapDescription');
    if (mapCanvasDescription) {
      mapCanvasDescription.textContent = accessibleDescription;
    }
  } catch (error) {
    console.warn('Failed to load surround description', error);
    app.bus.send('appInsights/log', {
      name: 'mapRenderer/descriptionError',
      properties: { error },
    });
  }
};

export const addSurroundTiles =
  (
    app: MapRendererApp,
    map: MapInstance,
    { venueCenter, surroundConfig }: { venueCenter: Coordinate; surroundConfig?: SurroundConfig | null },
    tileProtocol: Protocol,
    setHandlers: SetSurroundHandlers = () => {},
    isHighContrast: () => boolean = () => false,
  ) =>
  async () => {
    const runtimeApp = app as AppWithRuntimeBits;
    const log = runtimeApp.log.sublog('addSurroundTiles');
    const locale = runtimeApp.i18n().language ?? 'en';
    const { baseVenueId: venueCode } = (await app.bus.get('venueData/getVenueData')) as VenueDataWithBaseVenueId;

    const pmTilesUrl = `https://tiles.atrius.com/${venueCode}/map-surround-tile.pmtiles`;

    await setMapDescription(tileProtocol, pmTilesUrl, locale, app);

    map.addSource('surroundSource', {
      type: 'vector',
      url: `pmtiles://${pmTilesUrl}`,
    });

    // Dynamically find where to insert surround layers in the venue style stack
    const currentStyle = map.getStyle() as StyleDefinition;
    const beforeLayerId = findSurroundInsertionPoint(currentStyle);
    const labelBeforeLayerId = findSurroundLabelInsertionPoint(currentStyle);
    if (beforeLayerId === undefined) {
      log.warn('no conflicting layer found in venue style; skipping surround render');
      const sendResult = app.bus.send('appInsights/log', {
        name: 'mapRenderer/surroundTilesNotLoaded',
        properties: {
          venueId: venueCode,
        },
      }) as Promise<unknown> | undefined;
      sendResult?.catch(err => log.error('Failed to send appInsights event', err));
      return;
    }

    // Capture the venue style's original background + venue-polygon fill colors
    // so the high-contrast style can override them and we can restore them when
    // switching back. The default (Positron) style never touches these.
    const venuePolygonLayerIds = currentStyle.layers
      .filter(layer => layer.type === 'fill' && extractCategories(layer.filter).includes('basemap.venue'))
      .map(layer => layer.id);

    const captureOriginalColors = () => ({
      background: (() => {
        try {
          return map.getPaintProperty('background', 'background-color');
        } catch {
          return undefined;
        }
      })(),
      venueFills: new Map(
        venuePolygonLayerIds.map(id => {
          let fill;
          try {
            fill = map.getPaintProperty(id, 'fill-color');
          } catch {
            fill = undefined;
          }
          return [id, fill] as [string, unknown];
        }),
      ),
    });

    type OriginalColors = ReturnType<typeof captureOriginalColors>;
    let originalColors: OriginalColors | undefined;

    const applyStyleColors = (style: SurroundStyle) => {
      try {
        map.setPaintProperty('background', 'background-color', style.colors.background);
      } catch (e) {
        log.error('could not set background color', e);
      }
      for (const id of venuePolygonLayerIds) {
        try {
          map.setPaintProperty(id, 'fill-color', style.colors.background);
        } catch (e) {
          log.error(`could not set venue polygon fill for ${id}`, e);
        }
      }
    };

    const restoreOriginalColors = () => {
      if (!originalColors) {
        return;
      }
      try {
        map.setPaintProperty('background', 'background-color', originalColors.background);
      } catch (e) {
        log.error('could not restore background color', e);
      }
      for (const id of venuePolygonLayerIds) {
        try {
          map.setPaintProperty(id, 'fill-color', originalColors.venueFills.get(id) as unknown as string);
        } catch (e) {
          log.error(`could not restore venue polygon fill for ${id}`, e);
        }
      }
      originalColors = undefined;
    };

    const removeSurroundLayers = () => {
      map
        .getStyle()
        .layers.filter(layer => layer.id.startsWith(SURROUND_LAYER_PREFIX))
        .forEach(layer => map.removeLayer(layer.id));
    };

    const addSurroundLayers = () => {
      activeStyle.createLayers(locale).forEach((layer: SurroundLayer) => {
        const targetBeforeLayerId = ABOVE_BUILDING_LAYER_IDS.has(layer.id) ? labelBeforeLayerId : beforeLayerId;
        addLayer(map, layer as StyleLayer, targetBeforeLayerId);
      });
    };

    // The current surround style. Starts from mapRenderer's high-contrast mode;
    // the user menu toggle switches it later via the onToggle handler below.
    let activeStyle: SurroundStyle = isHighContrast() ? highContrastStyle : positronStyle;

    // (Re)render the surround for the active style. Safe to call repeatedly;
    // removes any previously added surround layers first.
    const renderSurround = (previousStyle?: SurroundStyle) => {
      removeSurroundLayers();
      // Each style owns its textured fill images: tear down the outgoing style's
      // patterns and let the incoming style register (and zoom-sync) its own.
      previousStyle?.patterns?.detach(map);
      activeStyle.patterns?.attach(map);
      addSurroundLayers();

      if (activeStyle.id !== positronStyle.id) {
        // Non-default style: capture the original colors on first switch-away,
        // then apply the new style's background and venue polygon fills.
        if (!originalColors) {
          originalColors = captureOriginalColors();
        }
        applyStyleColors(activeStyle);
      } else {
        // Switching back to default: restore whatever the venue had originally.
        restoreOriginalColors();
      }
    };

    renderSurround();

    // Build the runtime handlers for this venue's map and hand them to the
    // caller. The single 'surround/setHighContrast' and 'i18n/langChanged'
    // listeners live in mapRenderer.tsx and delegate here, so re-running this
    // function on a venue switch just replaces these handlers instead of adding
    // another bus subscription.

    // Swaps the surround style at runtime (e.g. the user toggling high-contrast
    // mode from the user menu).
    const onToggle: SurroundToggleHandler = ({ highContrast: enabled }: { highContrast?: boolean } = {}) => {
      const nextStyle = enabled ? highContrastStyle : positronStyle;
      if (nextStyle.id === activeStyle.id) {
        return;
      }
      const previousStyle = activeStyle;
      activeStyle = nextStyle;
      renderSurround(previousStyle);
    };

    // Layers whose text-field is locale-dependent and must be rebuilt on a
    // runtime language change.
    const localizedLabelLayers = [
      'positron-locality-labels',
      'positron-park-labels',
      'positron-water-labels-line',
      'positron-water-labels-point',
    ];

    // Updates locality, park, and water labels when the UI language changes
    // (SDK dynamic switch).
    const onLangChanged: LangChangedHandler = async () => {
      const newLocale = runtimeApp.i18n?.()?.language;
      try {
        await setMapDescription(tileProtocol, pmTilesUrl, newLocale, app);
        for (const layerId of localizedLabelLayers) {
          map.setLayoutProperty(layerId, 'text-field', localizedTextField(newLocale) as unknown as string);
        }
      } catch (e) {
        log.error('could not update surround labels on lang change', e);
      }
    };

    setHandlers({ onToggle, onLangChanged });

    // Apply pan clamp to limit tile data egress
    const maxPanMiles = surroundConfig?.clampMaxPanMiles ?? DEFAULT_MAX_PAN_MILES;
    const maxBounds = calculatePanClampBounds(venueCenter, maxPanMiles);
    map.setMaxBounds(maxBounds);
  };
